import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import shadcn components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Separator } from './components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { AlertCircle, Users, Trophy, Play, Settings, Plus, Crown, Building2, Sparkles, Star, Zap, Globe, Radio } from 'lucide-react';
import { toast } from 'sonner';

// Couche live (sessions temps réel)
import JoinPage from './live/JoinPage';
import HostLivePanel from './live/HostLivePanel';

// Vide = same-origin : nginx proxe /api sur le même domaine (staging/prod)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // You could validate token here by making a request to get current user
    }
    setLoading(false);
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Successfully logged in!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const register = async (name, email, password, role = 'spectator') => {
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        name, 
        email, 
        password, 
        role 
      });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Account created successfully!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Landing Page Component
const LandingPage = () => {
  const { login, register } = useAuth();
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (authMode === 'login') {
      await login(formData.email, formData.password);
    } else {
      await register(formData.name, formData.email, formData.password);
    }
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button 
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Trophy className="h-8 w-8 text-indigo-600" />
              <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                StoryBattle
              </span>
            </button>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => {
                  const element = document.getElementById('features');
                  const offset = 80; // Account for sticky nav
                  const elementPosition = element?.offsetTop;
                  const offsetPosition = elementPosition ? elementPosition - offset : 0;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }}
              >
                Features
              </Button>
              <Button 
                variant="ghost"
                onClick={() => {
                  const element = document.getElementById('pricing');
                  const offset = 80; // Account for sticky nav
                  const elementPosition = element?.offsetTop;
                  const offsetPosition = elementPosition ? elementPosition - offset : 0;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }}
              >
                Pricing
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>Get Started</Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {authMode === 'login' ? 'Welcome Back' : 'Create Account'}
                    </DialogTitle>
                    <DialogDescription>
                      {authMode === 'login' 
                        ? 'Sign in to your StoryBattle account' 
                        : 'Join the creative storytelling revolution'}
                    </DialogDescription>
                  </DialogHeader>
                  <Tabs value={authMode} onValueChange={setAuthMode}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="login">Sign In</TabsTrigger>
                      <TabsTrigger value="register">Sign Up</TabsTrigger>
                    </TabsList>
                    <TabsContent value="login" className="space-y-4">
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">Password</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="Enter your password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <Button type="submit" className="w-full">
                          Sign In
                        </Button>
                      </form>
                    </TabsContent>
                    <TabsContent value="register" className="space-y-4">
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Full Name</Label>
                          <Input
                            id="name"
                            name="name"
                            placeholder="Enter your full name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">Password</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="Create a password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <Button type="submit" className="w-full">
                          Create Account
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-8">
            <Badge variant="secondary" className="mb-4 px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Revolutionary Team Building
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Battle of Stories
              </span>
              <br />
              <span className="text-slate-900">Unleash Creativity</span>
            </h1>
            <p className="mt-8 text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Transform your team building with AI-powered creative battles. Teams craft stories from images, 
              spectators vote in real-time, and everyone wins with enhanced collaboration and engagement.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Dialog>
              <DialogTrigger asChild>
                <Button size="lg" className="px-8 py-4 text-lg">
                  <Play className="w-5 h-5 mr-2" />
                  Start Free Trial
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Create Account</DialogTitle>
                  <DialogDescription>
                    Start your 14-day free trial today
                  </DialogDescription>
                </DialogHeader>
                <Tabs value="register">
                  <TabsContent value="register" className="space-y-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Enter your full name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Enter your email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">Password</Label>
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        Start Free Trial
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
            <Button variant="outline" size="lg" className="px-8 py-4 text-lg">
              <Play className="w-5 h-5 mr-2" />
              Watch Demo
            </Button>
          </div>

          {/* Feature Cards */}
          <div id="features" className="grid md:grid-cols-3 gap-8 mt-20">
            <Card className="p-6 border-0 shadow-lg bg-white/60 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
              <CardContent className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold">Team Collaboration</h3>
                <p className="text-slate-600">
                  Teams work together in real-time to craft compelling stories from curated image prompts.
                </p>
              </CardContent>
            </Card>

            <Card className="p-6 border-0 shadow-lg bg-white/60 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
              <CardContent className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold">Live Voting</h3>
                <p className="text-slate-600">
                  Spectators join rooms instantly and vote on stories using customizable scoring criteria.
                </p>
              </CardContent>
            </Card>

            <Card className="p-6 border-0 shadow-lg bg-white/60 backdrop-blur-sm hover:shadow-xl transition-all duration-300">
              <CardContent className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto">
                  <Trophy className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold">Analytics & Insights</h3>
                <p className="text-slate-600">
                  Comprehensive reports on team engagement, creativity metrics, and collaboration patterns.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-slate-600">
              Scale from startup to enterprise with plans that grow with you
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {/* Free Trial */}
            <Card className="relative border-2 hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-lg">Free Trial</CardTitle>
                <CardDescription>Perfect for testing</CardDescription>
                <div className="text-3xl font-bold">$0<span className="text-base font-normal">/14 days</span></div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />1 Room</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />1 Active Battle</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />50 Spectators</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />Basic Support</li>
                </ul>
                <Button className="w-full">Start Free Trial</Button>
              </CardContent>
            </Card>

            {/* Pro */}
            <Card className="relative border-2 border-indigo-200 hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-lg">Pro</CardTitle>
                <CardDescription>For growing teams</CardDescription>
                <div className="text-3xl font-bold">$49<span className="text-base font-normal">/month</span></div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />5 Rooms</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />5 Active Battles</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />500 Spectators</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />50GB Storage</li>
                </ul>
                <Button className="w-full">Choose Pro</Button>
              </CardContent>
            </Card>

            {/* Business */}
            <Card className="relative border-2 border-purple-200 hover:shadow-lg transition-all duration-300">
              <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600" variant="default">
                Most Popular
              </Badge>
              <CardHeader>
                <CardTitle className="text-lg">Business</CardTitle>
                <CardDescription>For larger organizations</CardDescription>
                <div className="text-3xl font-bold">$149<span className="text-base font-normal">/month</span></div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />Unlimited Rooms</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />20 Active Battles</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />2,000 Spectators</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />SSO & White-label</li>
                </ul>
                <Button className="w-full">Choose Business</Button>
              </CardContent>
            </Card>

            {/* Enterprise */}
            <Card className="relative border-2 hover:shadow-lg transition-all duration-300">
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <Crown className="w-5 h-5 text-yellow-500 mr-2" />
                  Enterprise
                </CardTitle>
                <CardDescription>Custom solutions</CardDescription>
                <div className="text-3xl font-bold">Custom</div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />Unlimited Everything</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />Priority Support</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />Custom Integration</li>
                  <li className="flex items-center"><Star className="w-4 h-4 text-yellow-500 mr-2" />Dedicated Manager</li>
                </ul>
                <Button variant="outline" className="w-full">Contact Sales</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button 
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <Trophy className="h-8 w-8 text-indigo-400" />
              <span className="text-2xl font-bold">StoryBattle</span>
            </button>
            <div className="text-slate-400">
              © 2025 StoryBattle. Unleashing creativity worldwide.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleUpgrade = async (planType) => {
    if (planType === 'free_trial') {
      toast.success('You are already on the free trial!');
      return;
    }

    setSelectedPlan(planType);
    setLoading(true);

    try {
      // Get current URL for success and cancel URLs
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/payment-cancelled`;

      const response = await axios.post(`${API}/payments/checkout/session`, {
        plan_type: planType,
        success_url: successUrl,
        cancel_url: cancelUrl
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment session creation failed');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Subscription Status */}
            <Card className="border-l-4 border-l-indigo-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <Crown className="w-5 h-5 text-yellow-500 mr-2" />
                      Subscription Status
                    </CardTitle>
                    <CardDescription>
                      Current plan: <Badge variant="secondary">{user?.role?.replace('_', ' ').toUpperCase()}</Badge>
                    </CardDescription>
                  </div>
                  <Button onClick={() => setShowUpgradeModal(true)}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                </div>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Battles</CardTitle>
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">0</div>
                  <p className="text-xs text-muted-foreground">No active battles</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Teams</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">0</div>
                  <p className="text-xs text-muted-foreground">Create your first team</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Spectators Today</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">0</div>
                  <p className="text-xs text-muted-foreground">No spectators yet</p>
                </CardContent>
              </Card>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle>Welcome to StoryBattle!</CardTitle>
                <CardDescription>
                  Get started by creating your first room and battle.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-4">
                  <Button onClick={() => setActiveTab('rooms')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Room
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('teams')}>
                    <Users className="w-4 h-4 mr-2" />
                    Manage Teams
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Upgrade Modal */}
            <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
              <DialogContent className="sm:max-w-4xl">
                <DialogHeader>
                  <DialogTitle>Upgrade Your Plan</DialogTitle>
                  <DialogDescription>
                    Choose the perfect plan for your team building needs
                  </DialogDescription>
                </DialogHeader>
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  {Object.entries(plans).map(([planType, planData]) => (
                    <Card key={planType} className={`relative ${planType === 'business' ? 'border-purple-200 ring-2 ring-purple-100' : ''}`}>
                      {planType === 'business' && (
                        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600">
                          Most Popular
                        </Badge>
                      )}
                      <CardHeader>
                        <CardTitle className="flex items-center">
                          {planType === 'enterprise' && <Crown className="w-5 h-5 text-yellow-500 mr-2" />}
                          {planData.name}
                        </CardTitle>
                        <div className="text-3xl font-bold">
                          ${planData.price}
                          <span className="text-base font-normal">/month</span>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Button
                          className="w-full"
                          variant={planType === 'business' ? 'default' : 'outline'}
                          onClick={() => handleUpgrade(planType)}
                          disabled={loading && selectedPlan === planType}
                        >
                          {loading && selectedPlan === planType ? (
                            <div className="spinner mr-2" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-2" />
                          )}
                          {planType === 'free_trial' ? 'Current Plan' : 'Upgrade'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        );
      case 'rooms':
        return <RoomManagement />;
      case 'teams':
        return <TeamManagement />;
      case 'live':
        return <HostLivePanel />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button 
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setActiveTab('overview')}
            >
              <Trophy className="h-8 w-8 text-indigo-600" />
              <span className="text-2xl font-bold text-indigo-600">StoryBattle</span>
            </button>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.name}
              </span>
              <Badge variant={user?.role === 'super_admin' ? 'default' : 'secondary'}>
                {user?.role?.replace('_', ' ').toUpperCase()}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-8">
          {/* Sidebar */}
          <div className="w-64 space-y-2">
            <Button
              variant={activeTab === 'overview' ? 'default' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('overview')}
            >
              <Settings className="w-4 h-4 mr-2" />
              Overview
            </Button>
            <Button
              variant={activeTab === 'rooms' ? 'default' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('rooms')}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Rooms
            </Button>
            <Button
              variant={activeTab === 'teams' ? 'default' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('teams')}
            >
              <Users className="w-4 h-4 mr-2" />
              Teams
            </Button>
            <Button
              variant={activeTab === 'battles' ? 'default' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('battles')}
            >
              <Trophy className="w-4 h-4 mr-2" />
              Battles
            </Button>
            <Button
              variant={activeTab === 'live' ? 'default' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveTab('live')}
            >
              <Radio className="w-4 h-4 mr-2" />
              Live
            </Button>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Room Management Component
const RoomManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', max_spectators: 100 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      toast.error('Failed to fetch rooms');
    }
  };

  const createRoom = async () => {
    if (!newRoom.name) {
      toast.error('Please enter a room name');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/rooms`, newRoom);
      toast.success('Room created successfully');
      setShowCreateRoom(false);
      setNewRoom({ name: '', max_spectators: 100 });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Rooms</h2>
        <Button onClick={() => setShowCreateRoom(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Room
        </Button>
      </div>

      <div className="grid gap-4">
        {rooms.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                No rooms created yet. Create your first room to get started!
              </div>
            </CardContent>
          </Card>
        ) : (
          rooms.map((room) => (
            <Card key={room.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{room.name}</CardTitle>
                    <CardDescription>
                      Room Code: {room.code} | Max Spectators: {room.max_spectators}
                    </CardDescription>
                  </div>
                  <Badge variant={room.is_live ? 'default' : 'secondary'}>
                    {room.is_live ? 'LIVE' : 'OFFLINE'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  Created: {new Date(room.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Room Modal */}
      <Dialog open={showCreateRoom} onOpenChange={setShowCreateRoom}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Room</DialogTitle>
            <DialogDescription>
              Create a room where teams can battle and spectators can watch
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="room-name">Room Name</Label>
              <Input
                id="room-name"
                value={newRoom.name}
                onChange={(e) => setNewRoom(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Epic Story Arena"
              />
            </div>
            <div>
              <Label htmlFor="max-spectators">Max Spectators</Label>
              <Input
                id="max-spectators"
                type="number"
                min="1"
                max="10000"
                value={newRoom.max_spectators}
                onChange={(e) => setNewRoom(prev => ({ ...prev, max_spectators: parseInt(e.target.value) }))}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateRoom(false)}>
                Cancel
              </Button>
              <Button onClick={createRoom} disabled={loading}>
                {loading && <div className="spinner mr-2" />}
                Create Room
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Team Management Component  
const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', members: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const createTeam = async () => {
    if (!newTeam.name) {
      toast.error('Please enter a team name');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/teams`, newTeam);
      toast.success('Team created successfully');
      setShowCreateTeam(false);
      setNewTeam({ name: '', members: [] });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Teams</h2>
        <Button onClick={() => setShowCreateTeam(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Team
        </Button>
      </div>

      <div className="grid gap-4">
        {teams.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                No teams created yet. Create your first team to get started!
              </div>
            </CardContent>
          </Card>
        ) : (
          teams.map((team) => (
            <Card key={team.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center">
                      <Users className="w-5 h-5 mr-2 text-indigo-600" />
                      {team.name}
                    </CardTitle>
                    <CardDescription>
                      Members: {team.members.length} | Created: {new Date(team.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {team.members.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {team.members.map((memberId, index) => (
                      <Badge key={index} variant="outline">
                        Member {index + 1}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">No members assigned yet</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Team Modal */}
      <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription>
              Create a team to participate in storytelling battles
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                value={newTeam.name}
                onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Creative Warriors"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateTeam(false)}>
                Cancel
              </Button>
              <Button onClick={createTeam} disabled={loading}>
                {loading && <div className="spinner mr-2" />}
                Create Team
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Payment Success Component
const PaymentSuccess = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, []);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setPaymentDetails(data);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // If payment is still pending, continue polling
      setStatus('processing');
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
      case 'processing':
        return (
          <div className="text-center">
            <div className="spinner mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </div>
        );
      case 'success':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">
              Your subscription has been activated. Welcome to StoryBattle Premium!
            </p>
            {paymentDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600">
                  Amount: ${paymentDetails.amount} {paymentDetails.currency.toUpperCase()}
                </p>
              </div>
            )}
            <Button onClick={() => window.location.href = '/'}>
              Go to Dashboard
            </Button>
          </div>
        );
      case 'expired':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Expired</h2>
            <p className="text-gray-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button onClick={() => window.location.href = '/'}>
              Return to Dashboard
            </Button>
          </div>
        );
      case 'timeout':
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Check Timeout</h2>
            <p className="text-gray-600 mb-6">
              We're still processing your payment. Please check your email for confirmation.
            </p>
            <Button onClick={() => window.location.href = '/'}>
              Return to Dashboard
            </Button>
          </div>
        );
      default:
        return (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Error</h2>
            <p className="text-gray-600 mb-6">
              There was an error processing your payment. Please try again.
            </p>
            <Button onClick={() => window.location.href = '/'}>
              Return to Dashboard
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        {renderContent()}
      </div>
    </div>
  );
};

// Payment Cancelled Component
const PaymentCancelled = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-gray-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Cancelled</h2>
        <p className="text-gray-600 mb-6">
          You cancelled the payment process. No charges were made to your account.
        </p>
        <Button onClick={() => window.location.href = '/'}>
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
};

// Super Admin Component
const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', plan: 'free_trial' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTenants();
    initializeSampleData();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/tenants`);
      setTenants(response.data);
    } catch (error) {
      toast.error('Failed to fetch tenants');
    }
  };

  const initializeSampleData = async () => {
    try {
      await axios.post(`${API}/init-sample-data`);
    } catch (error) {
      console.log('Sample data initialization skipped or failed');
    }
  };

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.slug) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/tenants`, newTenant);
      toast.success('Tenant created successfully');
      setShowCreateTenant(false);
      setNewTenant({ name: '', slug: '', plan: 'free_trial' });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tenant');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <button 
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => window.location.reload()}
            >
              <Crown className="h-8 w-8 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">Super Admin</span>
            </button>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
              <Badge variant="default">SUPER ADMIN</Badge>
              <Button variant="outline" size="sm" onClick={logout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Tenant Management</h1>
            <Button onClick={() => setShowCreateTenant(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Tenant
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {tenants.map((tenant) => (
            <Card key={tenant.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{tenant.name}</CardTitle>
                    <CardDescription>
                      Slug: {tenant.slug} | Plan: {tenant.plan}
                    </CardDescription>
                  </div>
                  <Badge variant={tenant.status === 'active' ? 'default' : 'destructive'}>
                    {tenant.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  Created: {new Date(tenant.created_at).toLocaleDateString()}
                  {tenant.trial_ends_at && (
                    <span className="ml-4">
                      Trial ends: {new Date(tenant.trial_ends_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create Tenant Modal */}
        <Dialog open={showCreateTenant} onOpenChange={setShowCreateTenant}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>
                Add a new client organization to the platform
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Company Name</Label>
                <Input
                  id="name"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug (subdomain)</Label>
                <Input
                  id="slug"
                  value={newTenant.slug}
                  onChange={(e) => setNewTenant(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                  placeholder="acme-corp"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Will be used as subdomain: {newTenant.slug}.storybattle.com
                </p>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowCreateTenant(false)}>
                  Cancel
                </Button>
                <Button onClick={createTenant} disabled={loading}>
                  {loading && <div className="spinner mr-2" />}
                  Create Tenant
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/join" element={<JoinPage />} />
            <Route path="*" element={<AppContent />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
};

const AppContent = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Trophy className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">Loading StoryBattle...</p>
        </div>
      </div>
    );
  }

  // Check for payment success/cancelled routes
  const path = window.location.pathname;
  if (path === '/payment-success') {
    return user ? <PaymentSuccess /> : <Navigate to="/" />;
  }
  if (path === '/payment-cancelled') {
    return user ? <PaymentCancelled /> : <Navigate to="/" />;
  }

  // Show appropriate dashboard based on user role
  if (user) {
    if (user.role === 'super_admin') {
      return <SuperAdminDashboard />;
    }
    return <Dashboard />;
  }

  return <LandingPage />;
};

export default App;